import React from 'react';
import { doc, setDoc } from 'firebase/firestore';

import { AuthContext } from '../../context/AuthContext';
import { EVENT_NAMES } from '../../utils/eventLogging/eventNames';
import { firestore } from '../../context/FirebaseConfig';
import { useTranslation } from '../../i18n/i18n';
import UserModalShell from './UserModalShell';

const INPUT_CLASS =
  'w-full min-h-11 rounded-xl px-3 py-2.5 text-base leading-5 bg-white border border-[#e1e5eb] text-[#2d3041] placeholder:text-[#90a1b9] dark:bg-[#0c121d] dark:border-[#3c3e53] dark:text-[#fafafa] dark:placeholder:text-[#90a1b9] focus:outline focus:outline-2 focus:outline-[rgba(37,99,235,0.7)] dark:focus:outline-[rgba(39,115,183,0.75)] box-border';

export default function AddUserModal({ onClose, onUserAdded }) {
  const { currentUser, logEventCustom } = React.useContext(AuthContext);
  const { t } = useTranslation(currentUser?.info?.languageCode);
  const [firstname, setFirstname] = React.useState('');
  const [lastname, setLastname] = React.useState('');

  function addNewUser() {
    const userKey = `${firstname}_${lastname}`
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // strip accent marks
      .replace(/[^a-z0-9]+/gi, '_') // replace non-alphanumeric chars with underscore
      .replace(/^_|_$/g, '') // trim leading/trailing underscores
      .toLowerCase();
    const settingsDoc = doc(firestore, 'clients', currentUser.company.company, 'client_settings', 'bale_feed_settings');

    setDoc(settingsDoc, { users: { [userKey]: { firstname, lastname } } }, { merge: true });

    // The key only; the entered first and last name are never logged.
    logEventCustom(EVENT_NAMES.OPERATOR_ADD, { operator_id: userKey });

    onUserAdded?.(userKey);
    onClose?.();
  }

  const canSave = Boolean(firstname.trim() && lastname.trim());

  return (
    <UserModalShell
      title={t('add_user_modal_title')}
      onClose={onClose}
      footer={
        <button
          type='button'
          disabled={!canSave}
          className='flex items-center justify-center text-lg leading-[120%] font-medium text-[#FFFFFF] dark:text-[#FAFAFA] rounded-lg bg-[#2563EB] hover:bg-[#1E4FBB] active:bg-[#113C91] dark:bg-[#2773B7] dark:hover:bg-[#1D649C] dark:active:bg-[#2773B7] py-2 px-4 h-11 box-border transition-all duration-300 disabled:opacity-50 disabled:pointer-events-none'
          onClick={addNewUser}
        >
          {t('add_user_modal_action')}
        </button>
      }
    >
      <div className='flex flex-col gap-1.5'>
        <label className='text-base leading-[120%] text-[#464255] dark:text-[#FAFAFA]'>
          {t('add_user_modal_first_name')}
        </label>
        <input
          type='text'
          className={INPUT_CLASS}
          value={firstname}
          placeholder={t('add_user_modal_first_name_placeholder')}
          onChange={(event) => setFirstname(event.target.value)}
        />
      </div>

      <div className='flex flex-col gap-1.5'>
        <label className='text-base leading-[120%] text-[#464255] dark:text-[#FAFAFA]'>
          {t('add_user_modal_last_name')}
        </label>
        <input
          type='text'
          className={INPUT_CLASS}
          value={lastname}
          placeholder={t('add_user_modal_last_name_placeholder')}
          onChange={(event) => setLastname(event.target.value)}
        />
      </div>
    </UserModalShell>
  );
}
